import type { ApiKeyScopeRule } from './api-key-scope-rules.service';

export type ApiKeyScopeGrant = {
  explicitRoles: string[];
  checkedRoles: string[];
  impliedRoles: string[];
  roles: string[];
  permissions: string[];
};

export type ApiKeyScopeRuleGroup = {
  key: string;
  label: string;
  allRule?: ApiKeyScopeRule;
  rules: ApiKeyScopeRule[];
};

export function resolveApiKeyScopeGrant(
  rules: ApiKeyScopeRule[],
  explicitRoles: string[]
): ApiKeyScopeGrant {
  const rulesBySlug = new Map<string, ApiKeyScopeRule>();
  for (const rule of rules) {
    if (!rulesBySlug.has(rule.RoleSlug)) rulesBySlug.set(rule.RoleSlug, rule);
  }

  const explicit = [...new Set(explicitRoles)];
  const explicitSet = new Set(explicit);
  const checkedRoles = new Set<string>();
  const impliedRoles = new Set<string>();
  const submittedRoles = new Set<string>();
  const permissions = new Set<string>();
  const expanded = new Set<string>();

  const visit = (roleSlug: string, submit: boolean) => {
    checkedRoles.add(roleSlug);
    if (submit) submittedRoles.add(roleSlug);
    if (!explicitSet.has(roleSlug)) impliedRoles.add(roleSlug);

    const expansionKey = `${roleSlug}:${submit ? 'submit' : 'include'}`;
    if (expanded.has(expansionKey)) return;
    expanded.add(expansionKey);

    const rule = rulesBySlug.get(roleSlug);
    if (!rule) return;
    for (const reference of rule.Includes ?? []) {
      if (reference.Type === 'role') visit(reference.Value, false);
    }
    for (const reference of rule.Requires ?? []) {
      if (reference.Type === 'role') visit(reference.Value, true);
      else permissions.add(reference.Value);
    }
  };

  for (const roleSlug of explicit) visit(roleSlug, true);

  return {
    explicitRoles: explicit,
    checkedRoles: [...checkedRoles],
    impliedRoles: [...impliedRoles],
    roles: [...submittedRoles],
    permissions: [...permissions],
  };
}

export function groupApiKeyScopeRules(rules: ApiKeyScopeRule[]): ApiKeyScopeRuleGroup[] {
  const groups = new Map<string, ApiKeyScopeRuleGroup>();
  const orderedRules = rules
    .filter((rule) => rule.IsVisible)
    .sort(
      (left, right) =>
        left.SortOrder - right.SortOrder || left.RoleSlug.localeCompare(right.RoleSlug)
    );

  for (const rule of orderedRules) {
    const group = groups.get(rule.GroupKey) ?? {
      key: rule.GroupKey,
      label: rule.GroupLabel,
      rules: [],
    };
    if (rule.IsGroupAll && !group.allRule) group.allRule = rule;
    else group.rules.push(rule);
    groups.set(rule.GroupKey, group);
  }

  return [...groups.values()];
}
